import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule, MatFormFieldAppearance } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TestStep } from '../../dto/test-step';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Subject, takeUntil } from 'rxjs';
import { TestStepService } from '../../service/test-step.service';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../../../common/component/dialog/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'dpi-test-step-details',
  imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatCardModule, MatIconModule,
    MatButtonModule, MatToolbarModule, MatTooltipModule, MatDialogModule, NgIf],
  templateUrl: './test-step-details.component.html',
  styleUrl: './test-step-details.component.scss'
})
export class TestStepDetailsComponent implements OnInit, OnDestroy {
  testStep: TestStep;
  fieldAppearance: MatFormFieldAppearance;
  destroyed$ = new Subject<void>(); // notifier of component being destroyed
  isReadonly: boolean;
  isContentChanged: boolean;
  private _dialogConfirm = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private testStepService: TestStepService, private router: Router) {
    this.testStep = this.notifyService.getSelectedTestStep();
    this.isReadonly = true;
    this.fieldAppearance = 'outline';
    this.isContentChanged = false;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.testStepSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.testStep = model;
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.onEditModeChange(res);
      });
  }

  onEditModeChange(isReadonly: boolean) {
    this.isReadonly = isReadonly;
    if (isReadonly) {
      this.fieldAppearance = 'outline';
    } else {
      this.fieldAppearance = 'fill';
    }
  }

  onContentChange() {
    this.isContentChanged = true;
  }

  createTestStep() {
    this.testStepService.createTestStep(this.testStep.test_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addModelToSytem(model);
        },
        error: err => { console.log(err); }
      });
  }

  updateTestStep() {
    this.isContentChanged = false; // Reset the state, and disable the button to prevent multiple clicks
    this.testStepService.updateTestStep(this.testStep)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.testStep = model;
          this.notifyService.updateTestStepSelection(model);
        },
        error: err => { console.log(err); }
      });
  }

  deleteTestStep() {
    this.testStepService.deleteTestStep(this.testStep.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeTestStepAfterDelete();
        },
        error: err => { console.log(err); }
      });
  }

  private addModelToSytem(model: TestStep) {
    this.testStep = model;
    this.notifyService.updateTestStepSelection(this.testStep);
    let listTestStep = this.notifyService.getListAllTestStep();
    listTestStep.push(model);
    this.notifyService.updateListAllTestStep(listTestStep);
  }

  private removeTestStepAfterDelete() {
    let listTestStep = this.notifyService.getListAllTestStep();
    const index = listTestStep.indexOf(this.testStep, 0);
    if (index > -1) {
      listTestStep.splice(index, 1);
    }
    this.notifyService.updateListAllTestStep(listTestStep);
    //this.notifyService.updateTestStepSelection({ "id": 0, "test_id": 0, "sn": "" });

    // Select the current 'test', then navigate to 'test-hub', i.e. to load and display the list of test steps after deletion.
    let testModel = this.notifyService.getSelectedTest();
    this.notifyService.updateTestSelection(testModel); //update the same test selection, so that adaption of display features in 'app.component.ts' can be triggered. 
    this.router.navigate(['test-hub']);
  }


  btnDeleteClicked() {
    if (!this.isReadonly) {
      let message = "The whole test step will be deleted, sure to proceed?"
      const dialogRef = this._dialogConfirm.open(ConfirmDialogComponent, { data: message });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.confirmed) {
              this.deleteTestStep();
            }
          };
        });
    }
  }

}
